import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notifyApiError } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  type BookingSettings,
  type Connection,
  fieldClass,
  panelClass,
} from "../bookings/types";
interface Calendar {
  id: string;
  name: string;
  writable: boolean;
  meetingSupported: boolean;
}
function ProviderCard({
  connection,
  reload,
}: {
  connection: Connection;
  reload: () => void;
}) {
  const [choices, setChoices] = useState<Calendar[]>([]);
  const [destination, setDestination] = useState(
    connection.settings?.calendarId || "",
  );
  const [busy, setBusy] = useState<string[]>(
    connection.settings?.busyCalendarIds || [],
  );
  const [meetingUrl, setMeetingUrl] = useState(
    connection.settings?.meetingUrl || "",
  );
  const [username, setUsername] = useState(connection.settings?.username || "");
  const [password, setPassword] = useState("");
  const [region, setRegion] = useState(
    connection.settings?.region || "godaddy",
  );
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const run = async (action: () => Promise<void>) => {
    setWorking(true);
    setMessage("");
    try {
      await action();
    } catch (e) {
      notifyApiError(e);
    } finally {
      setWorking(false);
    }
  };
  return (
    <section className={panelClass}>
      <h3 className="font-semibold">
        {connection.provider === "titan"
          ? "Titan / GoDaddy Professional Email"
          : connection.provider === "google"
            ? "Google Calendar"
            : "Microsoft 365"}
      </h3>
      <p className="text-sm text-text-secondary">
        {connection.connected
          ? connection.settings?.meetingSupported
            ? "Connected and ready for bookings"
            : "Connected — select calendars to finish setup"
          : connection.configured
            ? "Connect your work calendar with your own account."
            : "Administrator setup needed before you can connect."}
      </p>
      {connection.provider === "titan" && (
        <div className="space-y-3">
          <label className="block text-sm">
            Mailbox address
            <input
              type="email"
              autoComplete="username"
              className={fieldClass}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Mailbox or app password
            <input
              type="password"
              autoComplete="current-password"
              className={fieldClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Email service
            <select
              className={fieldClass}
              value={region}
              onChange={(e) => setRegion(e.target.value as typeof region)}
            >
              <option value="godaddy">GoDaddy Professional Email</option>
              <option value="global">Titan</option>
              <option value="eu">Titan Europe</option>
            </select>
          </label>
          <p className="text-sm text-text-secondary">
            Use your mailbox password, which may differ from your GoDaddy
            account password. Credentials are stored encrypted. Configure
            Bookings → Email before publishing Titan bookings.
          </p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={working || !connection.configured}
          onClick={() =>
            void run(async () => {
              if (connection.provider === "titan") {
                try {
                  await api.post("/bookings/connections/titan", {
                    username,
                    password,
                    region,
                  });
                  reload();
                } finally {
                  setPassword("");
                }
                return;
              }
              const r = await api.post<{ url: string }>(
                `/bookings/oauth/${connection.provider}/start`,
              );
              window.location.assign(r.url);
            })
          }
        >
          {connection.connected ? "Reconnect" : "Connect"}
        </Button>
        {connection.connected && (
          <>
            <Button
              variant="outline"
              disabled={working}
              onClick={() =>
                void run(async () => {
                  setChoices(
                    await api.get<Calendar[]>(
                      `/bookings/calendars/${connection.provider}`,
                    ),
                  );
                })
              }
            >
              Choose calendars
            </Button>
            <Button
              variant="ghost"
              disabled={working}
              onClick={() =>
                void run(async () => {
                  await api.delete(
                    `/bookings/calendars/${connection.provider}`,
                  );
                  reload();
                })
              }
            >
              Disconnect
            </Button>
          </>
        )}
      </div>
      {!!choices.length && (
        <div className="space-y-3">
          <label className="block text-sm">
            Create meetings in
            <select
              className={fieldClass}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              <option value="">Choose calendar</option>
              {choices
                .filter(
                  (c) =>
                    c.writable &&
                    (connection.provider === "titan" || c.meetingSupported),
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          {connection.provider === "titan" && (
            <label className="block text-sm">
              Meeting link
              <input
                type="url"
                placeholder="https://…"
                className={fieldClass}
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
              />
              <span className="block mt-1 text-text-secondary">
                Paste a Teams, Meet, or other meeting link you control. This
                link is reused for your Titan bookings; a new video room is not
                generated.
              </span>
            </label>
          )}
          <fieldset>
            <legend className="text-sm mb-2">Check for conflicts in</legend>
            {choices.map((c) => (
              <label key={c.id} className="flex gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={busy.includes(c.id)}
                  onChange={(e) =>
                    setBusy(
                      e.target.checked
                        ? [...busy, c.id]
                        : busy.filter((id) => id !== c.id),
                    )
                  }
                />
                {c.name}
              </label>
            ))}
          </fieldset>
          <Button
            disabled={
              working ||
              !destination ||
              (connection.provider === "titan" && !meetingUrl)
            }
            onClick={() =>
              void run(async () => {
                await api.put(`/bookings/calendars/${connection.provider}`, {
                  calendarId: destination,
                  ...(connection.provider === "titan" ? { meetingUrl } : {}),
                  busyCalendarIds: [...new Set([...busy, destination])],
                });
                setMessage("Calendar choices saved.");
                reload();
              })
            }
          >
            Save calendars
          </Button>
        </div>
      )}
      {message && (
        <p role="status" className="text-emerald-400">
          {message}
        </p>
      )}
    </section>
  );
}
export function CalendarIntegrationSettings() {
  const [settings, setSettings] = useState<BookingSettings>();
  const reload = () => {
    void api
      .get<BookingSettings>("/bookings/me")
      .then(setSettings)
      .catch(notifyApiError);
  };
  useEffect(reload, []);
  return (
    <div className="space-y-4">
      <p className="text-text-secondary">
        Connect Titan, Google Calendar or Microsoft 365, then choose which
        calendars should block your available times. Only availability is shared
        with visitors.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        {settings?.providers.map((c) => (
          <ProviderCard key={c.provider} connection={c} reload={reload} />
        ))}
      </div>
      {!settings && <p>Loading calendar connections…</p>}
      <Link className="text-emerald-400 underline" to="/bookings">
        Manage booking pages and appointments
      </Link>
    </div>
  );
}
